import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { ErrorCard } from '@/components/ErrorCard'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import { InlineText } from '@/components/patterns/InlineText'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { ColorSwatchPicker } from '../components/ColorSwatchPicker'
import { IconPicker } from '../components/IconPicker'
import { LabelChip } from '../components/LabelChip'
import { addLabel, deleteLabel, listManagedLabels, restoreLabel, updateLabel } from './api'
import type { CrmManagedLabel } from './types'

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback
}

function usageText(label: CrmManagedLabel): string {
  const { contacts, organisations } = label.usage
  return `${contacts} ${contacts === 1 ? 'contact' : 'contacten'} · ${organisations} ${organisations === 1 ? 'organisatie' : 'organisaties'}`
}

/** Labels: no order (sorted by name), optional icon; delete can be undone. */
export function LabelTable() {
  const [rows, setRows] = useState<CrmManagedLabel[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [deleting, setDeleting] = useState<CrmManagedLabel | null>(null)
  const [busy, setBusy] = useState(false)
  const [newName, setNewName] = useState('')
  const nameField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    listManagedLabels()
      .then((data) => {
        if (!cancelled) {
          setRows(data)
          setFailed(false)
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  /** Saves one change; a failure is thrown for the caller to show where the change was made. */
  async function save(label: CrmManagedLabel, patch: Parameters<typeof updateLabel>[1]) {
    const updated = await updateLabel(label.id, patch)
    setRows((current) => current?.map((row) => (row.id === updated.id ? updated : row)) ?? null)
  }

  /** For the pickers, which have no place of their own to show a failure. */
  function savePicked(label: CrmManagedLabel, patch: Parameters<typeof updateLabel>[1]) {
    save(label, patch).catch((caught: unknown) => {
      toast.error(errorText(caught, 'Opslaan mislukt.'))
    })
  }

  async function add() {
    const name = newName.trim()
    if (!name) return
    try {
      const created = await addLabel({ name })
      setRows((current) =>
        [...(current ?? []), created].sort((a, b) => a.name.localeCompare(b.name, 'nl')),
      )
      setNewName('')
      // The button is disabled again now: the focus stays where the next name goes.
      nameField.current?.focus()
    } catch (caught) {
      toast.error(errorText(caught, 'Toevoegen mislukt.'))
    }
  }

  async function confirmDelete(label: CrmManagedLabel) {
    setBusy(true)
    try {
      await deleteLabel(label.id)
      setDeleting(null)
      setRows((current) => current?.filter((row) => row.id !== label.id) ?? null)
      toastUndo(`Label '${label.name}' verwijderd.`, async () => {
        await restoreLabel(label.id)
        setReloadKey((key) => key + 1)
      })
    } catch (caught) {
      toast.error(errorText(caught, 'Verwijderen mislukt.'))
    } finally {
      setBusy(false)
    }
  }

  if (failed) return <ErrorCard onRetry={() => setReloadKey((key) => key + 1)} />
  if (rows === null) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nog geen labels.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Voorbeeld</TableHead>
              <TableHead>Naam</TableHead>
              <TableHead>Kleur</TableHead>
              <TableHead>Icoon</TableHead>
              <TableHead>Gebruikt</TableHead>
              <TableHead>
                <span className="sr-only">Acties</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <LabelChip label={row} />
                </TableCell>
                <TableCell>
                  <InlineText
                    label={`Naam van ${row.name}`}
                    value={row.name}
                    required
                    maxLength={60}
                    onSave={(name) => save(row, { name: name ?? row.name })}
                  />
                </TableCell>
                <TableCell>
                  <ColorSwatchPicker
                    label={`Kleur van ${row.name}`}
                    value={row.color}
                    onChange={(color) => savePicked(row, { color })}
                  />
                </TableCell>
                <TableCell>
                  <IconPicker
                    label={`Icoon van ${row.name}`}
                    value={row.icon}
                    allowNone
                    onChange={(icon) => savePicked(row, { icon })}
                  />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{usageText(row)}</TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`${row.name} verwijderen`}
                      onClick={() => setDeleting(row)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <form
        className="flex max-w-md gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          void add()
        }}
      >
        <Input
          ref={nameField}
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="Naam van het nieuwe label"
          aria-label="Naam nieuw label"
          maxLength={60}
        />
        <Button type="submit" disabled={!newName.trim()}>
          <Plus />
          Label toevoegen
        </Button>
      </form>
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setDeleting(null)
          }}
          title={`Label '${deleting.name}' verwijderen?`}
          description={`Het label staat op ${usageText(deleting)}.`}
          busy={busy}
          onConfirm={() => void confirmDelete(deleting)}
        />
      ) : null}
    </div>
  )
}
